import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Typography,
    Box,
    CircularProgress,
    Alert,
} from '@mui/material';
import { Device } from '@types';

interface DeviceDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (device: Partial<Device>) => Promise<void>;
    device?: Device | null;
    apiaries?: any[];
}

const DeviceDialog: React.FC<DeviceDialogProps> = ({
    open,
    onClose,
    onSave,
    device,
    apiaries = [],
}) => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<Partial<Device>>({
        name: '',
        key: '',
        hardware_id: '',
        firmware_version: '',
        type: 'smartnyuki', // Always SmartNyuki Base
        hive: undefined,
    });

    useEffect(() => {
        if (device) {
            setFormData({
                name: device.name,
                key: device.key,
                hardware_id: device.hardware_id || '',
                firmware_version: device.firmware_version || '',
                type: 'smartnyuki',
                hive: device.hive || device.hive_id,
            });
        } else {
            // Generate a random key for new devices
            const randomKey = generateRandomKey(16);
            setFormData({
                name: '',
                key: randomKey,
                hardware_id: '',
                firmware_version: '',
                type: 'smartnyuki',
                hive: undefined,
            });
        }
    }, [device, open]);

    const generateRandomKey = (length: number): string => {
        const possible = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjklmnpqrstuvwxyz0123456789';
        let text = '';
        for (let i = 0; i < length; i++) {
            text += possible.charAt(Math.floor(Math.random() * possible.length));
        }
        return text.toLowerCase();
    };

    const handleChange = (field: string) => (event: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value,
        }));
    };

    const handleSubmit = async () => {
        // Name is always required. Key is only required for new devices.
        if (!formData.name || (!device && !formData.key)) {
            return;
        }

        setLoading(true);
        try {
            // If editing, omit the key from the payload as it's write-only on the backend
            const submitData = { ...formData };
            if (device) {
                delete submitData.key;
            }
            
            await onSave(submitData);
            onClose();
        } catch (error) {
            console.error('Error saving device:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {device ? 'Edit Device' : 'Configure SmartNyuki Device'}
            </DialogTitle>

            <DialogContent>
                <Box sx={{ pt: 2 }}>
                    {/* Info Alert */}
                    {!device && (
                        <Alert severity="info" sx={{ mb: 3 }}>
                            <strong>Device Setup:</strong> Power on your SmartNyuki device and connect to its WiFi network
                            (SmartNyuki_XXXXXX). Complete the setup wizard on the device, then your device will appear here automatically.
                        </Alert>
                    )}

                    <Grid container spacing={3}>
                        {/* Device Name */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Device Name"
                                value={formData.name}
                                onChange={handleChange('name')}
                                required
                                helperText="Give your device a friendly name (e.g., 'Apiary 1 Hive 3')"
                                placeholder="SmartNyuki Device"
                            />
                        </Grid>

                        {/* Device Serial / Key */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Device Serial / Key"
                                value={formData.key}
                                onChange={handleChange('key')}
                                required
                                disabled={!!device}
                                helperText={device ? "Serial key cannot be changed after registration." : "Enter the exact Device Serial outputted by your ESP32 Gateway."}
                                placeholder="e.g., ESP_12345"
                            />
                        </Grid>

                        {/* Assign to Hive - Tree Structure */}
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Assign to Hive (Optional)</InputLabel>
                                <Select
                                    value={formData.hive || ''}
                                    onChange={handleChange('hive')}
                                    label="Assign to Hive (Optional)"
                                    renderValue={(selected) => {
                                        if (!selected) return <em>None - Assign Later</em>;

                                        // Find the selected hive and its apiary
                                        for (const apiary of apiaries) {
                                            const hive = apiary.hives?.find((h: any) => h.id === selected);
                                            if (hive) {
                                                return `${hive.name} (${apiary.name})`;
                                            }
                                        }
                                        return selected;
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                maxHeight: 400,
                                            }
                                        }
                                    }}
                                >
                                    <MenuItem value="">
                                        <em>None - Assign Later</em>
                                    </MenuItem>

                                    {apiaries.map((apiary) => [
                                        // Apiary header (disabled, acts as group label)
                                        <MenuItem
                                            key={`apiary-${apiary.id}`}
                                            disabled
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: '0.9rem',
                                                color: 'text.primary',
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                '&.Mui-disabled': {
                                                    opacity: 1,
                                                }
                                            }}
                                        >
                                            ▼ {apiary.name}
                                        </MenuItem>,
                                        // Hives under this apiary
                                        ...(apiary.hives?.map((hive: any) => (
                                            <MenuItem
                                                key={`hive-${hive.id}`}
                                                value={hive.id}
                                                sx={{
                                                    pl: 4,
                                                    fontSize: '0.85rem',
                                                }}
                                            >
                                                {hive.name}
                                            </MenuItem>
                                        )) || [])
                                    ])}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading || !formData.name}
                >
                    {loading ? <CircularProgress size={20} /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeviceDialog;
